import { useMemo, useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useChitChatStatus, type ChitChatStatus } from "@/hooks/useChitChatStatus";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Label } from "@/components/ui/label";
import { CcAvatar, StatusRing, CcEmpty, AfroDivider } from "./primitives";
import { Plus, Eye, Trash2, Sparkles, Users, Globe2, UserCheck, UserMinus } from "lucide-react";
import { cn } from "@/lib/utils";

const BG_PRESETS = [
  "linear-gradient(135deg, hsl(22 82% 52%), hsl(14 68% 48%))",
  "linear-gradient(135deg, hsl(38 78% 52%), hsl(22 82% 52%))",
  "linear-gradient(135deg, hsl(152 32% 28%), hsl(22 30% 14%))",
  "linear-gradient(135deg, hsl(8 76% 56%), hsl(14 68% 48%))",
  "linear-gradient(135deg, hsl(24 12% 16%), hsl(22 30% 14%))",
];

type ProfileLite = { user_id: string; full_name: string | null; avatar_url: string | null };

type PrivacyMode = "all" | "contacts" | "only" | "except";

const PRIVACY_OPTIONS: { value: PrivacyMode; label: string; hint: string; icon: React.ReactNode }[] = [
  { value: "all", label: "Everyone", hint: "Any signed-in user can see this", icon: <Globe2 className="h-4 w-4" /> },
  { value: "contacts", label: "My contacts", hint: "Visible to people you chat with", icon: <Users className="h-4 w-4" /> },
  { value: "only", label: "Only share with…", hint: "Pick exactly who can view", icon: <UserCheck className="h-4 w-4" /> },
  { value: "except", label: "Contacts except…", hint: "Hide from selected people", icon: <UserMinus className="h-4 w-4" /> },
];

export function StatusPanel() {
  const { user } = useAuth();
  const { statuses, createTextStatus, deleteStatus, recordView } = useChitChatStatus();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [bg, setBg] = useState(BG_PRESETS[0]);
  const [privacy, setPrivacy] = useState<PrivacyMode>("contacts");
  const [audienceIds, setAudienceIds] = useState<string[]>([]);
  const [contacts, setContacts] = useState<ProfileLite[]>([]);
  const [profilesById, setProfilesById] = useState<Record<string, ProfileLite>>({});
  const [viewer, setViewer] = useState<ChitChatStatus | null>(null);
  const [viewersList, setViewersList] = useState<{ viewer_id: string; viewed_at: string }[]>([]);

  // Load profiles for status authors
  useEffect(() => {
    const ids = Array.from(new Set(statuses.map((s) => s.user_id)));
    const missing = ids.filter((id) => !profilesById[id]);
    if (!missing.length) return;
    supabase
      .from("profiles")
      .select("user_id, full_name, avatar_url")
      .in("user_id", missing)
      .then(({ data }) => {
        if (!data) return;
        setProfilesById((prev) => {
          const next = { ...prev };
          (data as ProfileLite[]).forEach((p) => (next[p.user_id] = p));
          return next;
        });
      });
  }, [statuses]);

  // Load contact list (chat partners) once composer opens with a granular mode
  useEffect(() => {
    if (!open || !user || (privacy !== "only" && privacy !== "except")) return;
    if (contacts.length) return;
    (async () => {
      const { data: members } = await supabase
        .from("chitchat_members")
        .select("conversation_id")
        .eq("user_id", user.id);
      const convIds = (members ?? []).map((m: any) => m.conversation_id);
      if (!convIds.length) return;
      const { data: peers } = await supabase
        .from("chitchat_members")
        .select("user_id")
        .in("conversation_id", convIds)
        .neq("user_id", user.id);
      const peerIds = Array.from(new Set((peers ?? []).map((p: any) => p.user_id)));
      if (!peerIds.length) return;
      const { data: profs } = await supabase
        .from("profiles")
        .select("user_id, full_name, avatar_url")
        .in("user_id", peerIds);
      setContacts((profs as ProfileLite[]) ?? []);
    })();
  }, [open, privacy, user, contacts.length]);

  const grouped = useMemo(() => {
    const mine = statuses.filter((s) => s.user_id === user?.id);
    const others = statuses.filter((s) => s.user_id !== user?.id);
    return { mine, others };
  }, [statuses, user?.id]);

  const resetComposer = () => {
    setText("");
    setPrivacy("contacts");
    setAudienceIds([]);
  };

  const submit = async () => {
    if (!text.trim()) return;
    const audience = privacy === "only" || privacy === "except" ? audienceIds : [];
    await createTextStatus(text.trim(), bg, privacy, audience);
    resetComposer();
    setOpen(false);
  };

  const toggleAudience = (id: string) => {
    setAudienceIds((prev) => (prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]));
  };

  const openViewer = async (s: ChitChatStatus) => {
    setViewer(s);
    await recordView(s.id);
    if (s.user_id === user?.id) {
      const { data } = await supabase
        .from("chitchat_status_views")
        .select("viewer_id, viewed_at")
        .eq("status_id", s.id);
      setViewersList((data as any) ?? []);
    } else {
      setViewersList([]);
    }
  };

  return (
    <div className="chitchat-theme p-4 chitchat-pattern-soft min-h-full">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-bold text-cc-ink">Status</h2>
          <p className="text-xs text-cc-ink-soft">Stories disappear after 24 hours</p>
        </div>
        <Button className="chitchat-btn-primary" onClick={() => setOpen(true)}>
          <Plus className="h-4 w-4" /> New
        </Button>
      </div>

      <AfroDivider className="my-3" />

      {/* My Status */}
      <section className="mb-6">
        <h3 className="text-sm font-semibold text-cc-ink-soft mb-2">My status</h3>
        {grouped.mine.length === 0 ? (
          <button
            className="chitchat-surface w-full p-3 flex items-center gap-3 hover:bg-cc-surface-2 transition"
            onClick={() => setOpen(true)}
          >
            <div className="relative">
              <CcAvatar id={user?.id} name="Me" size={48} />
              <span className="absolute -bottom-1 -right-1 h-5 w-5 rounded-full chitchat-brand-bg flex items-center justify-center text-cc-cream text-xs font-bold ring-2 ring-cc-surface">
                +
              </span>
            </div>
            <div className="text-left">
              <div className="font-medium text-cc-ink">My status</div>
              <div className="text-xs text-cc-ink-soft">Tap to add status update</div>
            </div>
          </button>
        ) : (
          <div className="grid grid-cols-3 gap-3">
            {grouped.mine.map((s) => (
              <StatusCard key={s.id} status={s} onClick={() => openViewer(s)} />
            ))}
          </div>
        )}
      </section>

      <h3 className="text-sm font-semibold text-cc-ink-soft mb-2">Recent updates</h3>
      {grouped.others.length === 0 ? (
        <CcEmpty
          icon={<Sparkles className="h-7 w-7 text-cc-cream" />}
          title="No status updates yet"
          hint="When friends share something, you'll see it here."
        />
      ) : (
        <div className="grid grid-cols-3 gap-3">
          {grouped.others.map((s) => (
            <StatusCard key={s.id} status={s} profile={profilesById[s.user_id]} onClick={() => openViewer(s)} />
          ))}
        </div>
      )}

      {/* Composer */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="chitchat-theme max-w-md">
          <DialogHeader>
            <DialogTitle>New text status</DialogTitle>
          </DialogHeader>
          <div
            className="rounded-2xl p-6 min-h-[200px] flex items-center justify-center text-center text-white text-xl font-semibold"
            style={{ background: bg }}
          >
            <Textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Type a message..."
              className="bg-transparent border-none text-white placeholder:text-white/70 text-center text-xl resize-none focus-visible:ring-0"
              maxLength={200}
              rows={3}
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            {BG_PRESETS.map((g) => (
              <button
                key={g}
                onClick={() => setBg(g)}
                className={cn(
                  "h-9 w-9 rounded-full ring-2 transition",
                  bg === g ? "ring-cc-burnt scale-110" : "ring-transparent",
                )}
                style={{ background: g }}
                aria-label="Background"
              />
            ))}
          </div>

          {/* Privacy picker */}
          <div className="space-y-2">
            <Label className="text-xs font-semibold text-cc-ink-soft uppercase tracking-wide">
              Who can view this
            </Label>
            <Select value={privacy} onValueChange={(v) => { setPrivacy(v as PrivacyMode); setAudienceIds([]); }}>
              <SelectTrigger className="bg-cc-surface-2 border-cc-border">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PRIVACY_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    <div className="flex items-center gap-2">
                      {opt.icon}
                      <div>
                        <div className="font-medium">{opt.label}</div>
                        <div className="text-xs text-muted-foreground">{opt.hint}</div>
                      </div>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {(privacy === "only" || privacy === "except") && (
              <div className="rounded-xl border border-cc-border bg-cc-surface-2 p-2">
                <div className="text-xs text-cc-ink-soft mb-2 px-1">
                  {privacy === "only"
                    ? `Share with ${audienceIds.length} selected`
                    : `Hide from ${audienceIds.length} selected`}
                </div>
                <ScrollArea className="h-40">
                  <div className="space-y-1 pr-2">
                    {contacts.length === 0 ? (
                      <p className="text-xs text-cc-ink-soft p-2">No contacts yet — start a chat first.</p>
                    ) : (
                      contacts.map((c) => {
                        const checked = audienceIds.includes(c.user_id);
                        return (
                          <label
                            key={c.user_id}
                            className="flex items-center gap-2 p-1.5 rounded-lg hover:bg-cc-surface cursor-pointer"
                          >
                            <Checkbox checked={checked} onCheckedChange={() => toggleAudience(c.user_id)} />
                            <CcAvatar id={c.user_id} name={c.full_name} src={c.avatar_url} size={28} />
                            <span className="text-sm text-cc-ink truncate">{c.full_name || "Unknown"}</span>
                          </label>
                        );
                      })
                    )}
                  </div>
                </ScrollArea>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { resetComposer(); setOpen(false); }}>Cancel</Button>
            <Button
              className="chitchat-btn-primary"
              onClick={submit}
              disabled={!text.trim() || ((privacy === "only" || privacy === "except") && audienceIds.length === 0)}
            >
              Share
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Viewer */}
      <Dialog open={!!viewer} onOpenChange={(v) => !v && setViewer(null)}>
        <DialogContent className="chitchat-theme max-w-md p-0 overflow-hidden">
          {viewer && (
            <div>
              <div
                className="min-h-[60vh] flex items-center justify-center p-6 text-white text-2xl font-semibold text-center"
                style={{ background: viewer.bg_color || BG_PRESETS[0] }}
              >
                {viewer.media_type === "text" && viewer.content}
                {viewer.media_type === "image" && viewer.media_url && (
                  <img src={viewer.media_url} alt="status" className="max-h-[60vh] rounded-xl" />
                )}
              </div>
              <div className="p-4 flex items-center justify-between gap-2">
                <div className="flex items-center gap-3 text-sm text-cc-ink-soft">
                  <span className="flex items-center gap-1.5">
                    <Eye className="h-4 w-4" /> {viewersList.length} view{viewersList.length === 1 ? "" : "s"}
                  </span>
                  {viewer.user_id === user?.id && (
                    <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-cc-surface-2 border border-cc-border">
                      {PRIVACY_OPTIONS.find((o) => o.value === viewer.privacy)?.icon}
                      {PRIVACY_OPTIONS.find((o) => o.value === viewer.privacy)?.label ?? viewer.privacy}
                    </span>
                  )}
                </div>
                {viewer.user_id === user?.id && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-cc-sunset"
                    onClick={async () => {
                      await deleteStatus(viewer.id);
                      setViewer(null);
                    }}
                  >
                    <Trash2 className="h-4 w-4 mr-1" /> Delete
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatusCard({
  status,
  profile,
  onClick,
}: {
  status: ChitChatStatus;
  profile?: ProfileLite;
  onClick: () => void;
}) {
  const expired = new Date(status.expires_at).getTime() < Date.now();
  return (
    <button
      onClick={onClick}
      className="chitchat-surface aspect-[3/4] overflow-hidden relative text-left group"
      style={{ background: status.bg_color || "hsl(var(--cc-surface-2))" }}
    >
      {status.media_type === "image" && status.media_url ? (
        <img src={status.media_url} alt="" className="absolute inset-0 w-full h-full object-cover" />
      ) : (
        <div
          className="absolute inset-0 flex items-center justify-center p-3 text-center text-white font-semibold text-sm leading-snug"
          style={{ background: status.bg_color || "hsl(var(--cc-burnt))" }}
        >
          <span className="line-clamp-6">{status.content}</span>
        </div>
      )}
      <div className="absolute top-2 left-2 flex items-center gap-1.5">
        <StatusRing seen={expired} size={32}>
          <CcAvatar id={status.user_id} name={profile?.full_name} src={profile?.avatar_url} size={26} />
        </StatusRing>
      </div>
      <div className="absolute bottom-0 inset-x-0 p-2 bg-gradient-to-t from-black/60 to-transparent text-white text-xs">
        {profile?.full_name || "You"}
      </div>
    </button>
  );
}
